import { useId, useState, type KeyboardEvent } from 'react'

export interface NumberStepperProps {
  label: string
  /** The value as TEXT, exactly as the user typed it. */
  value: string
  onChange: (next: string) => void
  min: number
  max?: number
  /** Increment for the buttons. Typed input is never constrained by it. */
  step: number
  /** "hours", "cups" -- rendered beside the field and read with it. */
  unit?: string
  hint?: string
  error?: string | null
  disabled?: boolean
  /**
   * The value can be emptied and nothing else: the buttons and arrow keys are
   * off, and an edit is accepted only when it removes characters (Backspace on
   * `30` gives `3`, then nothing; select-all + Delete gives nothing). Typing or
   * replacing a character is refused. A held number that contradicts another
   * answer stays visible until the user withdraws it (JOURNAL-FIX-01,
   * JOURNAL-FIX-02). The input itself is not `disabled`, because a disabled
   * input cannot be cleared.
   */
  clearOnly?: boolean
  /**
   * The id of an element elsewhere on the page that also describes this field,
   * appended to the field's own descriptions. Used to read the JOURNAL-FIX-01
   * note with Minutes while it can only be cleared (JOURNAL-FIX-02); not
   * `aria-readonly`, which would announce "read only" for a field that can be
   * emptied.
   */
  describedBy?: string
}

/** `next` is `held` with one contiguous run removed: a step towards empty. */
function isDeletion(held: string, next: string): boolean {
  if (next.length >= held.length) return false
  let p = 0
  while (p < next.length && next[p] === held[p]) p += 1
  return held.endsWith(next.slice(p))
}

/**
 * A number you can leave blank.
 *
 * ── WHY NOT `type="number"` ─────────────────────────────────────────────────
 *
 * Its value-sanitising algorithm yields `''` for anything it cannot parse, so
 * `"12abc"` silently becomes nothing at all. Invisibly destroying input is the one
 * thing a journal form must not do -- a journal is hand-entered and
 * unreconstructable. Its native spinner is also far under the 44px §7.3 requires.
 * So: `type="text"`, `inputMode="decimal"` for the right keypad, and two real
 * buttons.
 *
 * ── WHY THE VALUE IS A STRING ───────────────────────────────────────────────
 *
 * Three reasons, and the first two are correctness rather than taste.
 *
 * `''` has to stay distinguishable from `'0'`. Every numeric journal field treats
 * 0 as a real answer -- "no caffeine" is not "did not say" -- so a control that
 * parses eagerly and holds `number | null` has to invent a second channel to carry
 * the difference, and `Number('')` is 0, which is the wrong one by default.
 *
 * And a controlled input whose value is re-derived from a parsed number fights the
 * user: `'8.'` and `'.5'` are unrepresentable as numbers mid-keystroke, so the
 * character disappears as it is typed.
 *
 * Lastly it keeps the string-to-number conversion in exactly one place --
 * `readNumeric` in `lib/selfReport.ts` -- rather than in every change handler.
 *
 * ── AND WHY THE BUTTONS CLAMP WHILE TYPING DOES NOT ─────────────────────────
 *
 * A button is an affordance: it offers the next legal value, so stopping at the
 * bound is what it means. Silently rewriting a typed 25 into 24 is not the same
 * act -- it puts a number in the record that the person did not enter. Typed
 * out-of-range text is left alone and reported as an error by the caller.
 *
 * The unit sits INSIDE the joined row rather than under it (Sprint 1.9.1,
 * DETAIL-05), and stays a `<span>` read through `aria-describedby`: a second
 * `<label for>` would append it to the field's accessible name.
 */
export function NumberStepper({
  label,
  value,
  onChange,
  min,
  max,
  step,
  unit,
  hint,
  error,
  disabled = false,
  clearOnly = false,
  describedBy: extraDescription,
}: NumberStepperProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const unitId = `${id}-unit`
  // Held as state, not a ref: typing clears it, a button press sets it, and the
  // render reads only state. A ref read during render is what
  // `react-hooks/refs` forbids, and it would also not re-render the region.
  const [announced, setAnnounced] = useState('')

  const describedBy = [hint ? hintId : null, unit ? unitId : null, error ? errorId : null, extraDescription ?? null]
    .filter(Boolean)
    .join(' ')

  // `step` is authored as a literal, so its own text is the authority on how many
  // decimals the result may carry. Without this, 7 + 0.25 * 3 prints as
  // 7.749999999999999 and the field appears to corrupt itself.
  const decimals = String(step).split('.')[1]?.length ?? 0
  const round = (n: number) => Number(n.toFixed(decimals))

  const current = value.trim() === '' ? null : Number(value.replace(',', '.'))
  const usable = current !== null && Number.isFinite(current)
  // Nothing is stepped from an unreadable value: the alternative is replacing
  // what the user typed with a guess.
  const canStep = (value.trim() === '' || usable) && !clearOnly

  /**
   * `steps` rather than a loop at the call site: `value` is a prop, so it does not
   * change between two synchronous calls in one handler. Four calls would all
   * compute from the same stale number and the last would win, which is how a
   * PageUp of four ends up moving by one.
   */
  const nudge = (direction: 1 | -1, steps = 1) => {
    // From blank, the first press offers the bottom of the range rather than
    // assuming a starting point.
    const base = usable ? (current as number) : direction === 1 ? min - step : min
    let next = round(base + direction * step * steps)
    if (next < min) next = min
    if (max !== undefined && next > max) next = max
    setAnnounced(`${label} ${next}${unit ? ` ${unit}` : ''}`)
    onChange(String(next))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const big = event.key === 'PageUp' || event.key === 'PageDown'
    const up = event.key === 'ArrowUp' || event.key === 'PageUp'
    const down = event.key === 'ArrowDown' || event.key === 'PageDown'
    if (!up && !down) return
    // The guard comes BEFORE `preventDefault`, and it did not.
    //
    // With the order reversed, a value this control cannot parse made the key
    // vanish: the default was cancelled and then nothing happened. ArrowUp and
    // ArrowDown losing their caret behaviour is small; PageUp and PageDown
    // losing THEIR default means pressing Page Down in a journal field with an
    // unparseable number does not scroll the page, which reads as a frozen
    // screen. Cancel a default only when something is being done instead.
    if (!canStep) return
    event.preventDefault()
    nudge(up ? 1 : -1, big ? 4 : 1)
  }

  return (
    <div className="field stepper">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="stepper__row">
        <button
          type="button"
          className="stepper__btn"
          onClick={() => nudge(-1)}
          disabled={disabled || !canStep}
          aria-label={`Decrease ${label}`}
        >
          &minus;
        </button>
        <input
          id={id}
          className="field__control stepper__input"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          placeholder="—"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          onChange={(event) => {
            // Under `clearOnly` the only accepted edit removes characters, one
            // Backspace at a time or all at once. Checked here and not in a
            // keydown handler: Android soft keyboards often report
            // `key: "Unidentified"`, and the resulting text is what matters.
            if (clearOnly && !isDeletion(value, event.target.value)) return
            setAnnounced('')
            onChange(event.target.value)
          }}
          onKeyDown={onKeyDown}
        />
        {/*
          INSIDE the row, between the value and the + button, and still a
          `<span>`. A second `<label for>` here would make the accessible name
          "Sleep hours hours" and break every `getByLabel(..., { exact: true })`
          in the journal's specs; `aria-describedby` already reads it with the
          field, which is how a unit is meant to reach a screen reader.
        */}
        {unit && (
          <span className="stepper__unit" id={unitId}>
            {unit}
          </span>
        )}
        <button
          type="button"
          className="stepper__btn"
          onClick={() => nudge(1)}
          disabled={disabled || !canStep}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      )}
      {/*
        A button press changes a value the user is not focused on, so nothing
        would be announced. Typing is deliberately NOT announced -- it is already
        echoed, and a live region would repeat every keystroke.
      */}
      <span className="visually-hidden" role="status">
        {announced}
      </span>
    </div>
  )
}
